import {
  AdminSpinner,
  Button,
  Div,
  H3,
  Modal,
  ModalIconStyle,
  P,
  SuccessToast,
  TrashIcon,
  clearMenu,
  reportError,
} from '@vgrubs/components';
import { MenuNamesResponse } from 'components/src/types/api/menu.types';
import { MenuV2Response } from 'components/src/types/menu.types';
import { useState } from 'react';
import { useSelectedMenu } from '../menuSelection';

type Props = {
  isOpen: boolean;
  closeModal: () => void;
  locationId: string | number;
  brandId: string | number;
  storeName: MenuNamesResponse | null;
  /** Receives the emptied menu the backend answers with. */
  onCleared: (menu: MenuV2Response) => void;
};

/**
 * Destructive, no undo: the only way back is Publish History -> Restore, which
 * brings back the last published version. The confirmation names the target
 * so nobody clears the wrong store by accident.
 */
export const ClearMenuModal = ({
  isOpen,
  closeModal,
  locationId,
  brandId,
  storeName,
  onCleared,
}: Props) => {
  const [loading, setLoading] = useState(false);
  const { menuId, menu: selectedMenu } = useSelectedMenu();

  const target = storeName
    ? storeName.brandName
      ? `${storeName.brandName} @ ${storeName.locationName}`
      : storeName.locationName
    : 'this store';

  const handleClear = async () => {
    try {
      setLoading(true);
      const { data } = await clearMenu(locationId, brandId, menuId);
      onCleared(data.data);
      SuccessToast(data.message);
      closeModal();
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <AdminSpinner />}

      <Modal isOpen={isOpen} closeModal={closeModal} logo={false}>
        <Div $flex $justifyCenter>
          <ModalIconStyle>
            <TrashIcon />
          </ModalIconStyle>
        </Div>
        <H3 $mb="8">
          {selectedMenu
            ? `Clear the "${selectedMenu.name}" menu?`
            : 'Clear the entire menu?'}
        </H3>
        <P $mb="16">
          This will delete all categories, items, modifiers and availabilities
          {selectedMenu ? ` of the "${selectedMenu.name}" menu` : ''} for{' '}
          <b>{target}</b>.
          {selectedMenu && ' Other menus of this brand are not touched.'} This
          cannot be undone.
        </P>
        <P $mb="16">
          Nothing is pushed to the providers until you publish. The only way
          back is Publish History, which restores the last published version.
        </P>

        <Div $flex $gap="20px">
          <Div $width="50%">
            <Button $selected $w="100%" $mt="24" onClick={handleClear}>
              Clear Menu
            </Button>
          </Div>
          <Div $width="50%">
            <Button $selected $gray $w="100%" $mt="24" onClick={closeModal}>
              Cancel
            </Button>
          </Div>
        </Div>
      </Modal>
    </>
  );
};
